import { useState, useEffect } from 'react';
import type { Player } from '../types';
import { filterPlayersWithSummary } from '../utils/playerValue';

export function usePlayers() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPlayers() {
      try {
        // Fetch sleeper player data
        const resSleeper = await fetch('https://api.sleeper.app/v1/players/nfl');
        const data = (await resSleeper.json()) as Record<string, any>;
        // Fetch ESPN athlete headshots (optional - if this fails, we'll use placeholders)
        const headshotMap: Record<string, string> = {};
        try {
          const resEspn = await fetch(
            'https://sports.core.api.espn.com/v3/sports/football/nfl/athletes?limit=20000&active=true'
          );
          const espnResponse = await resEspn.json();
          
          // Handle ESPN API response structure - it might be { items: [...] } or an array
          const espnData = Array.isArray(espnResponse) ? espnResponse : (espnResponse.items || []);
          console.log('DEBUG usePlayers: ESPN data structure:', { 
            isArray: Array.isArray(espnResponse), 
            hasItems: !!espnResponse.items,
            itemsLength: espnData.length 
          });
          
          espnData.forEach((a: any) => {
            if (a.fullName && a.headshot?.href) {
              headshotMap[a.fullName] = a.headshot.href;
            }
          });
        } catch (espnError) {
          console.log('DEBUG usePlayers: ESPN API failed, using placeholder images:', espnError);
          // Continue without headshots - not critical for functionality
        }
        // Map players and include imageUrl from ESPN or placeholder
        const rawPlayers = Object.values(data);
        console.log('DEBUG usePlayers: Starting with', rawPlayers.length, 'raw players');
        
        const validPlayers = filterPlayersWithSummary(rawPlayers, 'usePlayers', true);
        const filteredPlayers = validPlayers
          // Additional filters for this specific use case
          .filter((p) => {
            // Filter out players on IR (too specific for general validation)
            if (p.injury_status === 'Out' || p.injury_status === 'IR') return false;

            // Filter out players with excessive experience (optional)
            if (p.years_exp !== null && p.years_exp > 15) return false;

            return true;
          });
        
        console.log('DEBUG usePlayers: After filtering:', filteredPlayers.length, 'players');
        
        const list: Player[] = filteredPlayers
          .map((p) => ({
            id: p.player_id, // Keep as string to match Player interface
            name: p.full_name,
            position: p.position as Player['position'],
            team: p.team,
            value: 0,
            projection: 0, // Required by Player interface
            lastYearPoints: 0, // Required by Player interface - correct field name
            imageUrl: headshotMap[p.full_name] || '/images/player-placeholder.png',
          }));
        console.log('DEBUG usePlayers: Created', list.length, 'players from', Object.keys(data).length, 'raw players');
        setPlayers(list);
      } catch (err: any) {
        console.error('DEBUG usePlayers error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchPlayers();
  }, []);

  return { players, loading, error };
}
